import { z } from 'zod';

/**
 * Primitives and the dashboard-plane error envelope.
 *
 * Conventions used throughout this package:
 *  - request bodies are `.strict()` (server-design.md §3 preamble);
 *  - timestamps are ISO-8601 with offset; calendar dates are `YYYY-MM-DD`
 *    (the substrate stores `date` for content dates — §8);
 *  - money is micro-USD as a JSON number (`bigint` in Postgres; run and month
 *    totals stay far inside the safe-integer range).
 */

export const Uuid = z.guid();
export type Uuid = z.infer<typeof Uuid>;

/** ISO-8601 instant, e.g. `2026-08-26T17:04:11.000Z`. */
export const Timestamp = z.iso.datetime({ offset: true });
export type Timestamp = z.infer<typeof Timestamp>;

/**
 * Calendar date, `YYYY-MM-DD` — content dates, window bounds, `as_of`.
 *
 * **The shape and then the calendar**, because the shape alone is not enough.
 * The pattern accepts `2026-13-45`: four digits, two, two. Postgres' `date`
 * does not, so a string that got this far reached the column and came back as a
 * `22008` — which the envelope filter reports as `INTERNAL` with a Sentry
 * capture, on the app's own notes door and on `add_note` alike. A caller who
 * mistyped a date is not an incident, and a refusal that names the field is a
 * thing the caller (or the model relaying for them) can act on, where "internal
 * error" is not.
 *
 * The check is a round trip rather than a table of month lengths: `Date`'s UTC
 * parse normalises an overflow (`2026-02-30` becomes 2 March), so a string that
 * comes back out of `toISOString()` unchanged is a date the calendar has and
 * nothing else is. Leap years, month lengths and the day-zero case all fall out
 * of it. The regex stays in front, so the message a caller gets still
 * distinguishes "not the format" from "not a date".
 *
 * **Except year zero, which the round trip lets through and Postgres does
 * not.** There is no year 0000 in the proleptic Gregorian calendar the `date`
 * type implements — 1 BC is followed by AD 1 — but `Date` is happy to parse
 * `0000-01-01` and hand it straight back, so the one string the refinement
 * cannot catch is the one that reaches the column and comes back a `22008`.
 * `(?!0000)` in front of the year is the whole of the fix; `9999-12-31`, the
 * other end, is a date Postgres has and stays accepted.
 */
export const DateString = z
  .string()
  .regex(/^(?!0000)\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Not a calendar date.');
export type DateString = z.infer<typeof DateString>;

/**
 * A calendar date at the precision the source stated it: `YYYY`, `YYYY-MM` or
 * `YYYY-MM-DD`.
 *
 * It exists so that a **window** can say exactly what the source said. The
 * catalog's own rule for a window bound is "a calendar date as the source
 * states it, never inferred", and a wire that only carried `YYYY-MM-DD` forced
 * the projection to break it: a seat a roster dated `2021` came across as
 * `2021-01-01` and was read out as "since Jan 2021", which is a month nobody
 * wrote down. Carrying the stated precision instead means the reader can print
 * "since 2021" — the record's actual answer — and nothing downstream has to
 * guess which part of a date was real.
 *
 * Deliberately narrow in scope. `last_corroborated`, `first_recorded_at`,
 * `content_date`, `as_of` and every other date on the wire stay strict
 * `DateString`: those are dates the *system* determined, not quotes from a
 * source, and a loose one there would be a bug rather than a precision.
 */
export const LooseDate = z
  .string()
  .regex(/^\d{4}(-\d{2}(-\d{2})?)?$/, 'expected YYYY, YYYY-MM or YYYY-MM-DD');
export type LooseDate = z.infer<typeof LooseDate>;

/** `YYYY-MM` — the usage rollup month. */
export const MonthString = z.string().regex(/^\d{4}-\d{2}$/, 'expected YYYY-MM');
export type MonthString = z.infer<typeof MonthString>;

/** Micro-USD (1e-6 USD), priced server-side. */
export const MicroUsd = z.number().int().nonnegative();
export type MicroUsd = z.infer<typeof MicroUsd>;

/** Opaque jsonb payload — fact `fields`, analysis `content`, anti-anchor attributes. */
export const JsonObject = z.record(z.string(), z.unknown());
export type JsonObject = z.infer<typeof JsonObject>;

/**
 * A resolved `app_user`, for attribution rendering ("Brian kept this a moment
 * ago"). `name` is nullable: `app_user.name` is nullable until the user has
 * signed in (data-model.md §2.1).
 */
export const UserRef = z.object({
  id: Uuid,
  name: z.string().nullable(),
});
export type UserRef = z.infer<typeof UserRef>;

/**
 * The same account with the one field attribution never needed: the **email**.
 *
 * It exists for the membership link (import note §1.14), where the answer being
 * rendered is *who this person is on the roster* rather than who did something.
 * `name` is null until a first sign-in, and "This is " with nothing after it is
 * not a sentence — so the reader is given something to fall back on rather than
 * a blank.
 */
export const LinkedUser = UserRef.extend({ email: z.email() });
export type LinkedUser = z.infer<typeof LinkedUser>;

/**
 * An org the caller belongs to. `id` is what `:orgId` routes take; `slug` is
 * display-only. `vertical` is the active vertical module (`club` in stage 1) —
 * the SPA renders the vertical's own surfaces off it, so the settings page knows
 * whether to offer the club profile without a second read.
 */
export const OrgRef = z.object({
  is_demo: z.boolean().optional(),
  id: Uuid,
  slug: z.string(),
  name: z.string(),
  vertical: z.string(),
});

/**
 * **The protection account a record belongs to**, as every directory row carries
 * it (security fold, 2026-09-22): the People list, the Organizations list and
 * the knowledge base's entries.
 *
 * Core rather than part of the security contract, because the *rows* are core: a
 * People row is a People row in five verticals, and a field that appeared and
 * disappeared with the org's type would be a second shape for every reader to
 * hold. The account is `null` in the four verticals that have none, which is a
 * statement rather than an omission — see the row types for why it is
 * `.nullable()` and required rather than `.optional()`.
 *
 * The **label** rides with the id because every use of it is a chip or a column:
 * a row that carried only the id would cost the client one lookup per distinct
 * account to render a word the server already had in hand.
 */
export const RecordAccount = z.object({ id: Uuid, label: z.string() });
export type RecordAccount = z.infer<typeof RecordAccount>;
export type OrgRef = z.infer<typeof OrgRef>;

/* ————————————————————————— errors ————————————————————————— */

/**
 * The conflict-code registry (server-design.md §7.8, amended 2026-08-26 to
 * include the two non-409 codes the dashboard also decodes).
 */
export const CONFLICT_CODES = [
  'SECURITY_ACCOUNT_CHANGED',
  'SECURITY_ACCOUNT_ARCHIVED',
  'OWNERSHIP_FENCE',
  'STATE_CAS_FAILED',
  'DECISION_ALREADY_ANSWERED',
  'IDEMPOTENCY_KEY_REUSED',
  'RUN_NOT_RESUMABLE',
  /**
   * **Cancel was pressed on a run that had already stopped** — `complete` or
   * `failed`, whichever it reached (owner ruling, 2026-09-10, the Cancel control
   * beside the status line). The office's press and the run's own last write race
   * every time a run finishes while somebody is reading the strip, so this is the
   * ordinary outcome of that race rather than an error worth a sentence: the
   * strip is about to lose its control anyway, and `details.state` says which
   * ending the run reached.
   */
  'RUN_NOT_CANCELLABLE',
  'RUN_ALREADY_ACTIVE',
  'IDENTITY_NOT_ESTABLISHED',
  /**
   * A prose rerun was asked for on a person with no current `facet_summary:*`
   * analyses — the substrate the summary stage writes from
   * (prose-rerun-design.md §4.4). It is deliberately *not* "no completed
   * research run exists": the two diverge after an identity unwind, which
   * supersedes every analysis and leaves the completed run row standing.
   */
  'NOTHING_TO_REWRITE',
  /**
   * A prose rerun was asked for while the person still has unanswered questions
   * on their brief (§6.2, decided 2026-08-31). Prose written before the answers
   * is prose to rewrite again, so the office is routed to the questions instead.
   * A *live* run with open questions already refuses with `RUN_ALREADY_ACTIVE`;
   * what this catches is rows that outlived a terminal run.
   */
  'QUESTIONS_AWAITING_ANSWER',
  /**
   * A person was entered as a new subject while the org already holds one or
   * more **live** person entities under the same normalized name (decided
   * 2026-09-01, after the Andrew East duplicate). A name is not an identity, so
   * the server refuses to guess: `details` carries the namesakes it found and
   * the caller re-sends with `entity_id` (adopt that record) or
   * `force_new: true` (a different human being who shares the name).
   */
  'NAME_CONFLICT',
  /**
   * The namesake question could not be *asked* — a pipeline batch was holding
   * the write lock on that name for longer than the create is willing to wait
   * (decided 2026-09-02, with the lock). Nothing is written and nothing is
   * decided: unlike `NAME_CONFLICT` this carries no question and no details,
   * because the server never got far enough to know whether there is one. The
   * same create sent again a moment later is the whole of the recovery.
   */
  'NAME_BUSY',
  /**
   * **A rename was asked for onto a name a live record of the same kind already
   * carries** (organisations-as-subjects note §2 chunk 1). Two live rows of one
   * name in one org is a legitimate state — a name is not an identity — but it
   * is not one a *rename* should create on purpose: the whole point of the
   * gesture is that the record ends up findable by the name it is actually
   * called, and a second row answering to that name is the ambiguity the office
   * was trying to remove.
   *
   * Nothing is written and nothing is decided. Unlike `NAME_CONFLICT` there is
   * no adopt or `force_new` door out of it — the record the office is standing
   * on is the one they meant, so the recourse is a different name, or joining
   * the two records. `details.issues` carries the sentence naming the name that
   * is taken, the same shape `VALIDATION_FAILED` carries its sentences in, so
   * one reader on the client covers both.
   */
  'NAME_TAKEN',
  /**
   * A "Fix it" correction the fact cannot take (2026-09-30): the fact is not
   * live, the field is not one the office fixes, the value is not a date, or
   * nothing changes. Nothing is written; the message is the office's sentence
   * and `details.reason` names which refusal it was.
   */
  'FIX_REFUSED',
  /**
   * **The record a pending rename proposal names is no longer there** — folded
   * into another record, or deleted, between the lookup proposing the name and
   * the office answering the card (organisations-as-subjects note §2 chunk 3
   * "As grounded" 7).
   *
   * A 409 and not a 404, on the same reasoning as the two below it: the
   * *decision* is real and the office is standing on its card. What is gone is
   * the thing it was about.
   *
   * It is a **refusal that rolls the answer back**, which is the whole reason it
   * has a code. The answer path latches `answered` before it runs consequences,
   * so a rename that cannot happen has to take the latch with it and leave the
   * question pending — otherwise the log would carry a confirmed rename that
   * never occurred, and the card would be gone from the inbox with nothing to
   * show for it.
   */
  'RENAME_TARGET_GONE',
  /**
   * **A run kind written about a person was asked for on an organisation**
   * (organisations-as-subjects note §2 chunk 3). `quick_search`, `research` and
   * `prose_refresh` are person kinds all the way down — the first establishes an
   * identity, the second is downstream of that lock and plans against the six
   * person facets, the third rewrites a person's brief — and none of them has a
   * meaning for a company.
   *
   * A **kind precondition** rather than a 404, and that is the point: the
   * subject exists and the office is standing on its record. What is wrong is
   * the gesture, so the sentence names the one that is right.
   *
   * **Widened from run kinds to any person-shaped gesture** (intake-timeout note,
   * open call 4). The second one is `POST /inputs/:id/extract-retry` pressed on an
   * organisation's own statement: the retry queues the *general* reader, which is
   * written about a person and carries none of the restriction the statements door
   * reads a company's paragraph under. The same shape as the run kinds — the
   * record is the one the office meant and the gesture is not — so the same code,
   * with the throw site supplying the sentence that names the door that is right.
   */
  'NOT_FOR_ORGANISATIONS',
  /**
   * **The lookup was asked for on a person.** The mirror of the code above, and
   * a separate one rather than a shared "wrong kind" because the two sentences
   * point in opposite directions: this one sends the office to Research.
   */
  'NOT_FOR_PEOPLE',
  /** **A person's or an organisation's run kind was asked for on an event.** */
  'NOT_FOR_EVENTS',
  /** **An attendee scan was asked for with nobody left to scan.** */
  'NOTHING_TO_SCAN',
  /** **An attendee scan in a vertical none of whose analyses needs one.** */
  'SCAN_NOT_OFFERED',
  /** **An event report named an event of another protection account.** */
  'EVENT_NOT_ON_ACCOUNT',
  /** **A scan was asked for while its analysis's report is still being written.** */
  'ANALYSIS_REPORT_IN_PROGRESS',
  /** **Write the report was asked for a scan whose report is already written.** */
  'ANALYSIS_REPORT_WRITTEN',
  /** **A report run was asked for while its analysis's scan, which writes it, still runs.** */
  'ANALYSIS_SCAN_IN_PROGRESS',
  /** **An event report was asked for before any attendee was scanned.** */
  'NOTHING_TO_SUMMARIZE',
  /** **A new version of a report whose event was deleted or untracked.** */
  'REPORT_EVENT_GONE',
  /** **An attendee already promoted into the knowledge base.** */
  'ATTENDEE_ALREADY_PROMOTED',
  /** **An attendee's email or phone already belongs to someone on the record.** */
  'IDENTIFIER_ON_RECORD',
  /** **The record refused the attendee's participation; the promotion rolled back.** */
  'ATTENDEE_NOT_RECORDED',
  /**
   * **An investigation was asked for on an organisation nobody has looked up
   * yet** (owner ruling, 2026-09-09: "Investigate should be downstream of
   * lookup. Similar to the brief vs id lock on people").
   *
   * The same shape as `IDENTITY_NOT_ESTABLISHED`, which is the rule it was
   * modelled on: a person's dossier is refused until a quick search has settled
   * who the dossier is about, and the four-leg fan-out over an organisation's
   * filings, finances and people is refused until a lookup has settled what the
   * organisation *is*. Four legs fanned at a name and a guess is the expensive
   * way of finding out that the record was a placeholder.
   *
   * It is checked **after** the active-run refusal, unlike the identity lock: a
   * lookup that is running right now will make this pass in a minute, and
   * "a run is already in progress" is the truer answer to somebody standing in
   * front of one. Any completed lookup clears it, at either depth.
   */
  'LOOKUP_FIRST',
  /**
   * An adopt (`entity_id`) named a person who is already a subject of their
   * own. Nothing is written — the caller wanted the record that already exists.
   */
  'ENTITY_ALREADY_SUBJECT',
  /**
   * An org was created on a slug another org already holds (decided 2026-09-02,
   * Soroush). The route used to answer find-or-create — the existing row, 201,
   * and a differing `name` or `vertical` in the body silently dropped. A slug is
   * a tenant's identity, so a second create on one is a conflict whatever else
   * the body says, and `details.slug` names the one that is taken.
   */
  'ORG_SLUG_TAKEN',
  /**
   * A member was linked to a person another member of the **same org** is
   * already linked to (import note §1.14, 2026-09-06). The link answers "which
   * person on the graph is this user of the system", and two users cannot be one
   * person: the record would then have to say "This is you" to two different
   * people. Nothing is written, and `details` names the membership that holds the
   * person, so the operator can unlink that one first if the second is the right
   * answer.
   *
   * Admin-plane like `ORG_SLUG_TAKEN`, and here for the same reason that one is:
   * `ConflictException` takes a `ConflictCode` and there is no server-local seam
   * for extending the union.
   */
  'PERSON_ALREADY_LINKED',
  'VALIDATION_FAILED',
  'STATUS_NOT_YET_SUPPORTED',
  /**
   * A `{candidate_ref}` answer named a candidate this decision cannot bind
   * (decided 2026-09-03, with the three ask tiers). Two roads reach it and both
   * are the same mistake: the ref is not in the row's `retained_candidates[]`,
   * or the row is an `over_fifteen` more-prose question, which retains none by
   * contract and can only be answered with prose. Nothing is written — the
   * answer never latched — and the message names which of the two it was.
   */
  'CANDIDATE_NOT_SELECTABLE',
  /**
   * The candidate a bind was asked for **carries nothing to identify anyone
   * by** (2026-09-03): no proposed anchors, and no match basis to derive them
   * from. Binding it would write a confirmed anchor-set version with an empty
   * membership — an identity that says the office settled this person while
   * naming nothing about them, and one that every later namesake sweep would
   * have no way to hold to. So the answer is refused instead, on the pick arm
   * and the held list's shortcut alike, and the row stays open.
   */
  'CANDIDATE_HAS_NO_ANCHORS',
  /**
   * The office answered "same" on a likely-merge question and the merge refused
   * (likely-merges note §1e). The merge folds **satellite** nodes only: a node
   * carrying research of its own, a record of its own, accreted identifying
   * detail or written analysis raises questions the merge has no standing to
   * answer on its own, so it names them and changes nothing. The answer is
   * rolled back with it — the question stays open, and an operator joins the two
   * with `entity:merge` once somebody has decided what survives.
   */
  'MERGE_REFUSED',
  /**
   * An unmerge was asked for and cannot run (merge triage and unmerge spec,
   * 2026-10-02, §2). `details.reason` is one of `UNMERGE_REFUSAL_REASONS` and the
   * message is the sentence the reader acts on. Nothing is written.
   */
  'UNMERGE_REFUSED',
  /**
   * An Undo on a "different" merge triage recorded without asking (merge triage
   * spec, 2026-10-02, §3) cannot run: it was undone already, the answer was not
   * triage's, or one of the two records has gone. Nothing is written.
   */
  'TRIAGE_UNDO_REFUSED',
  /**
   * A commit was asked for on an import session that has **not been previewed
   * against the plan it is holding** (import note §1.8, §1.9). The preview is
   * the dry run the office reads before it approves — it is where every count on
   * the Import tab comes from and where a namesake becomes a question — so a
   * commit that skipped it, or one sent after a plan edit that superseded it,
   * would be an approval of numbers nobody was shown. `details.plan_version` is
   * the version the session is holding.
   */
  'IMPORT_NOT_PREVIEWED',
  /**
   * A commit was asked for while rows still need a look — a namesake nobody
   * picked, or a name the shape checks caught (§1.8). Every such row is one
   * gesture away from an answer (pick, fix the name, set aside, or a bulk rule
   * over the filter), which is why this is a conflict and not a silent skip:
   * committing around them would file the office's open questions as people.
   * `details.count` and `details.locators` name the first of them.
   */
  'IMPORT_HAS_UNRESOLVED_ROWS',
  /**
   * **A commit was asked for while the dry run's findings about the whole file
   * stand unconfirmed** (§4.3, 2026-09-14). Its sibling above is about rows;
   * this one is about the file — names that repeat across most of it, names that
   * are the file's own headings, rows that would write a person and nothing
   * else. Each is a sentence with the file's own numbers in it, and none of them
   * is a thing the office can be shown one row at a time.
   *
   * `details.findings` carries the whole list, so a client that halted on a page
   * showing an older one has what it needs without a second read. The recourse
   * is to read them and press again with `confirm_findings` naming every kind:
   * they are a stop, not a refusal, because the file may well be right.
   *
   * **The truth at commit is the recompute**, not the copy stored on the session
   * — a finding that appears between the office's last look and the press is
   * refused rather than waved through.
   */
  'IMPORT_HALTED',
  /**
   * **A commit was asked for while a correction on the plan stands unapplied**
   * (§4.4, 2026-09-15). A correction says one cell of the file is to be read as
   * something other than what it says, and the executor applies it only where
   * the row is still there, still says what the correction says it said, and
   * still carries the name the correction names — the guard that keeps a
   * replaced file from writing yesterday's number onto today's neighbor.
   *
   * Any of those failing leaves the correction on the plan and the cell read as
   * the file has it, which is exactly the silence the section exists to remove:
   * the office asked for the number to be different and the commit would have
   * written the old one. So it is a stop, and unlike {@link IMPORT_HALTED} it
   * cannot be confirmed past — the recourse is to fix the correction or to take
   * it off the plan. `details.issues` carries the sentences, each naming the row
   * and the column and saying which half of the guard did not hold.
   */
  'IMPORT_CORRECTION_UNAPPLIED',
  /**
   * A session that has already written its people was asked to write them again
   * — a second commit, a cancel after the fact, a plan edit on a closed session.
   * The commit is the unit of undo (§1.9) and undo is what this is asking for:
   * the recourse is `POST /imports/:id/unwind`, which is what the message says.
   */
  'IMPORT_ALREADY_COMMITTED',
  /** An unwind was asked for on a session that never wrote anything (§1.9). */
  'IMPORT_NOT_COMMITTED',
  /**
   * **An unwind was asked for on a session already unwound** (§4.1). Distinct
   * from `IMPORT_NOT_COMMITTED`, which is the session that never wrote anything:
   * this one wrote, and the removal has already run and is in the decision log
   * as `import_unwind`. Saying so by its own code matters because the second
   * unwind is a **double press**, not a mistake — and running it again would
   * write a second unwind decision over a session whose reach has already been
   * taken back, negating nothing and reporting nothing kept, which reads to the
   * office like the first one having done less than it did.
   */
  'IMPORT_ALREADY_UNDONE',
  /**
   * An unwind was asked for while the commit's structural phase is **still
   * writing** (§1.9, §4.1). Unwind's whole method is to read what the session
   * wrote and take exactly that back, and half a session is not a thing that can
   * be read: rows still to come would land after the removal and stay, with the
   * session marked `undone` around them. It is a wait rather than a refusal —
   * the phase is short and the session page is already polling it — so the
   * gesture is to press the button again when the run strip says the people are
   * in.
   *
   * **Also the answer while the session is reading its notes** (`reading_notes`),
   * for the identical reason: the note-reading driver is still ingesting claims
   * against this import id, and every one of them would land after the removal.
   */
  'IMPORT_STILL_IMPORTING',
  /**
   * A replace's dry run read the event's list as some set of uploads, and that
   * set has changed since (another upload finished, or one was undone): the
   * diff the office read is not what the commit would do. Run the dry run again.
   */
  'IMPORT_REPLACE_STALE',
  /**
   * The file was asked to be replaced after the session committed it (§2.1).
   * Replacement exists for the export that turned out to be the wrong sheet, and
   * it re-parses, re-profiles and re-keys every row — which on a committed
   * session would leave the receipts pointing at rows that no longer exist. The
   * bytes a commit wrote from are the bytes its evidence cites.
   */
  'IMPORT_FILE_LOCKED',
  /**
   * The uploaded file is not one this build can open — an unknown container, or
   * a CSV, workbook or PDF whose bytes the parser refused. `details.container`
   * names what was inferred, and the message names the three that work. It is a
   * 400 rather than a 415 because the container is a field of a multipart body
   * rather than the request's own type.
   */
  'IMPORT_FILE_UNREADABLE',
  /**
   * A turn was asked of an import session that is no longer being planned
   * (2026-09-06, with the planner). A session that has been committed, canceled
   * or unwound is a record of what happened, not a conversation: its plan is the
   * one the executor obeyed, and a later version of it would describe a file that
   * was already imported under different instructions. Nothing is written and no
   * model is called — the office starts a new session on the same file with
   * `POST /imports/:id/retry`, which carries the plan this one settled and the
   * answers it was given about rows.
   */
  'IMPORT_NOT_EDITABLE',
  /**
   * **A retry was asked for on a session that is still open** (2026-09-14). The
   * mirror of `IMPORT_NOT_EDITABLE`: retry exists because a finished session
   * refuses every editing door, and one that is still `in_progress` refuses
   * none — a second session beside it would be two conversations about one file,
   * each holding a plan of its own, with nothing saying which of them the office
   * meant. The gesture is to carry on in the session that is already open.
   */
  'IMPORT_STILL_EDITABLE',
  /**
   * **A retry was asked for on a session that never held an upload**
   * (2026-09-14). Retry points a new session at the *stored bytes* of an old
   * one, so a session with no `import_file` row behind it has nothing to point
   * at. The one kind that has none is the batch `dossier:import` opens for
   * itself, which reads hand-written packages rather than a file anybody sent.
   */
  'IMPORT_HAS_NO_FILE',
  /**
   * **A retry was asked for on a session that already has one open**
   * (2026-09-14). Two sessions on one file, both editable, both holding a plan,
   * is the state nothing downstream can read: the office is looking at two Plan
   * tabs for one export with nothing saying which is the live one. The common
   * cause is a double press, and the recovery is the session that already
   * exists — `details.import_id` names it, so the sentence can send them there
   * rather than leaving them to find it on the ledger.
   *
   * It is deliberately narrow. Only an **open** retry refuses: a retry that was
   * itself finished — imported, undone, canceled — is a record like any other,
   * and starting again from the same source after it is an ordinary second pass
   * rather than a duplicate.
   */
  'IMPORT_ALREADY_RETRIED',
  /**
   * A turn was asked of a session whose transcript has grown past what one call
   * can usefully carry (2026-09-06). Every message is read into the model's
   * context on every later turn, so a session nobody ever closed is a bill that
   * climbs with each thing said and an answer that does not get better for it.
   * Sixty messages is well past where a conversation about one spreadsheet
   * settles.
   *
   * Nothing is written and no model is called. The recovery is a new session on
   * the same file, which starts from the plan this one already settled.
   */
  'IMPORT_SESSION_TOO_LONG',
  /**
   * The planner answered with a plan the contract refuses, twice (2026-09-06).
   * The model is re-asked once with the validation errors named, and this is what
   * a second failure is: no version is written, no phase moves, and the user's
   * own message stays on the transcript so the session is still usable — the same
   * text sent again is the whole of the recovery.
   *
   * A 502 rather than a 409: nothing about the request was wrong. `details`
   * carries `retryable: false` — see {@link ImportPlannerFailureDetails}.
   */
  'IMPORT_PLAN_INVALID',
  /**
   * The planner could not be reached at all (2026-09-06) — transport, a missing
   * credential, a 4xx from Vertex. Distinct from `IMPORT_PLAN_INVALID`, which
   * says a model answered and the answer was unusable: this one says nobody
   * answered, and the two want different things of the office. "The plan came
   * back in a shape this build cannot use" sent for a network failure reads as a
   * problem with what they asked, and it is not one.
   *
   * No agent turn is written and the same request sent again is the whole of
   * the recovery — `details` says so with `retryable: true`, see
   * {@link ImportPlannerFailureDetails}. On `/messages` the office's own message
   * was written before the model was called and stays written, answers and all,
   * so the retry names those answers under `changes` or the server refuses them
   * as already given (2026-09-15). `/open` writes nothing before the model
   * answers. Whatever an earlier attempt of the same turn spent is still metered.
   */
  'IMPORT_PLANNER_UNAVAILABLE',
  /**
   * **A question the transcript already settled was answered again** (import
   * note §6.3, 2026-09-15), and the body did not say the repeat was meant.
   *
   * Answered is a fact the server holds rather than a thing the page remembers:
   * a question is answered when a user message *later than the agent message
   * that asked it* carries an answer for its id. The office presses Send twice
   * on one batch, or sends from two tabs, and without this the same question
   * costs two turns and two model calls. `details` carries `question_ids` — the
   * ids that were already answered, and only those; see
   * {@link ImportQuestionAnsweredDetails}.
   *
   * A 409 and not a 400: the body is well-formed and was right when the office
   * built it. What moved is the session. Nothing is written — not the message,
   * not the answers that were fine — because a turn that dropped the answered
   * ones and kept the rest would answer as though the office had left them out.
   *
   * The client's move is to refetch the session, where the named questions now
   * render answered, and send the rest of the batch. Answering one of them on
   * purpose is `changes`, which names the ids the office means to change and
   * takes this refusal off them.
   */
  'IMPORT_QUESTION_ANSWERED',
  /**
   * **The website the office typed is not one** (organisations-as-subjects note
   * §2 chunk 2a, pass ii) — no dot in the host, whitespace inside it, or nothing
   * `new URL()` can read at all.
   *
   * A 400 rather than a zod refusal on the field, because the rule is not a
   * shape: `example` and `not a website` are both well-formed strings and
   * neither one names a host. `details.issues` carries the sentence in the plain
   * list `NAME_TAKEN` uses, so one reader on the client covers both — there is no
   * field path to point at that the reader is not already standing on.
   *
   * Nothing is written. On the create door that means **nothing at all**: the
   * refusal takes the whole transaction with it rather than leaving a half-made
   * organisation whose website did not stick.
   */
  'WEBSITE_UNREADABLE',
  /* **`WEBSITE_ALREADY_CONFIRMED` was here and is gone** (owner ruling,
     2026-09-10). It refused a second website on the grounds that a confirmed
     `domain` is identity and one company answers to one site — which is false of
     the company it was written for. Medici moved from `medici.md` to `medi.ci`,
     the 2018 release that names the old address is still about this company, and
     the office may say so. A second website is now confirmed beside the first,
     the earliest one is the record's primary, and every check reads the whole
     set. Nothing throws this code any more, so it is not in the enum: a code no
     server sends is a branch every client keeps forever. */
  /**
   * **The organization has spent its allowance for the calendar month**
   * (2026-09-09), so no new AI work starts until the month turns over or the
   * operator raises the limit.
   *
   * A ceiling on the whole account, compared against the ledger's sum over every
   * source for the month. Import sessions have no ceiling of their own (run
   * defaults sweep, 2026-09-26), so this is the one spend refusal an import
   * meets.
   *
   * `details` carries `spent_micro_usd`, `ceiling_micro_usd` and `month`. The two
   * figures are in micro-USD, the unit the ledger meters in.
   *
   * **The office is never shown either figure.** The dashboard maps this code to
   * a sentence with no amount in it and a place to go, because what an office
   * can do about an allowance it did not set is ask; the details are for the
   * operator reading a log.
   *
   * Nothing is written and nothing is queued. A run already parked stays parked
   * and resumable — the refusal stops work starting, never work already done.
   */
  'ORG_MONTHLY_SPEND_CEILING_REACHED',
  /**
   * **Live charging refused the press** (credits launch spec, step 1). Three
   * codes, each a different next move, and all of them distinct from the spend
   * ceiling above, which is the operator's runaway backstop rather than credit.
   *
   * `CREDITS_EXHAUSTED`: the monthly allowance is used and available credit is
   * short of the price. `CREDIT_PRICE_CHANGED`: the price is higher than the one
   * the press accepted (the allowance ran out since the page loaded); `details`
   * carries `units`, the price now. `PLAN_EXCLUDES_PAID_WORK`: the account's plan
   * starts no new paid work at all. Nothing is written and nothing is queued.
   */
  'CREDITS_EXHAUSTED',
  'CREDIT_PRICE_CHANGED',
  'PLAN_EXCLUDES_PAID_WORK',
  /**
   * **The retry door was pressed on a note whose words have moved**
   * (`docs/notes/2026-09-14-break-up-overlong-notes.md`).
   *
   * `notes:split` breaks an overlong note already on record into pieces of its
   * own and keeps the parent for the audit trail — the abandoned attempts and
   * the reasons they gave are the evidence of why it was split. The parent is
   * out of every reader, so nobody should be standing in front of one; the code
   * exists because a stored id outlives a screen (a bookmarked modal, the
   * connector holding an id from an earlier read), and re-reading the parent
   * would ingest the same words a second time under a note nothing lists.
   *
   * A 409 and not a 404: the row is there and the id is right. What is wrong is
   * the gesture, so the sentence sends the reader to the pieces, which is where
   * the words — and their own retry buttons — now are.
   */
  'NOTE_SUPERSEDED',
  /**
   * **A note was edited or removed by an owner and an id from before came back**
   * (2026-10-29): a second edit from a modal left open, a retry on the old
   * words. The row is kept for the audit trail and out of every reader, so the
   * sentence sends the reader back to the record, which holds what is current.
   */
  'NOTE_CHANGED',
  /**
   * **A run was sent to a route for the other target** (reports design §4.3).
   * A report run cannot post evidence, claims, analyses or decisions, or read
   * the subject context; a subject run cannot read a report context or post a
   * report version. Nothing is written.
   */
  'RUN_TARGET_MISMATCH',
  /**
   * **Someone saved a newer version of this report first.** An edit names the
   * version it was made against, and only the current one takes it. `details`
   * carries `current_version_id`; the client reloads and the office re-applies
   * its change.
   */
  'REPORT_VERSION_STALE',
  /** **The report is archived**, so it takes no new version or run. */
  'REPORT_ARCHIVED',
  /** Pipeline only: a collecting run posted a different job plan than it saved. */
  'REPORT_PLAN_CHANGED',
  /** Pipeline only: the collection job already has its outcome. */
  'REPORT_JOB_FINISHED',
  /** Pipeline only: a collection write from a run that does not collect (a revise). */
  'REPORT_RUN_DOES_NOT_COLLECT',
] as const;

export const ConflictCode = z.enum(CONFLICT_CODES);
export type ConflictCode = z.infer<typeof ConflictCode>;

/**
 * **Whether sending the same thing again is worth a try**, on the two planner
 * failures that share a status.
 *
 * Both are 502 and neither writes an agent turn, so the code alone does not
 * tell a client what to offer: `IMPORT_PLANNER_UNAVAILABLE` means nobody
 * answered and a moment later they might, while `IMPORT_PLAN_INVALID` means a
 * model answered twice with something unusable and a third identical request
 * would be a third unusable answer. The flag is on the wire rather than derived
 * from the code by each SPA, so a Try again button is one condition and not a
 * table of codes to keep in step.
 */
export interface ImportPlannerFailureDetails {
  readonly retryable: boolean;
}

/**
 * **Which questions the batch answered twice**, on `IMPORT_QUESTION_ANSWERED`.
 *
 * The ids and not a count: a batch settles several questions at once and the
 * page has to say which of them it is putting back — the rest of the batch is
 * still sendable, and a client told only that something was answered already
 * would have to throw the whole press away.
 */
export interface ImportQuestionAnsweredDetails {
  readonly question_ids: readonly string[];
}

/**
 * Every dashboard-plane error is `{error: {code, message, details?}}`
 * (decided 2026-08-26). `code` is a registry code for the cases the UI decodes;
 * it may be another string for ordinary 4xx/5xx.
 */
export const ErrorEnvelope = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;

/** `VALIDATION_FAILED` carries the Zod issues that rejected the request. */
export const ValidationFailedDetails = z.object({
  issues: z.array(
    z.object({
      path: z.array(z.union([z.string(), z.number()])),
      message: z.string(),
      /**
       * Zod's issue code where the refusal came from a parse, `null` where the
       * server built the issue itself. Always present: an optional key here would
       * make "this issue has no code" and "an older server that never sent one"
       * the same thing on the wire (see this package's README).
       */
      code: z.string().nullable(),
    }),
  ),
});
export type ValidationFailedDetails = z.infer<typeof ValidationFailedDetails>;
